import { ARCHETYPES, ELEMENTS, TRAITS, type Character } from '@drawgon/shared';
import { badge, badgeRed, badgeYellow, panel, sunken } from '../lib/ui';

const STAT_LABELS = [
  ['HP', 'hp'],
  ['ATK', 'atk'],
  ['DEF', 'def'],
  ['SPD', 'spd'],
] as const;

/**
 * キャラクターカード（企画書 §19）。
 *
 * SNS で共有したくなる単位として企画書が挙げているもの。今は一覧表示に使うだけだが、
 * 将来 OGP 画像の生成元にもなる想定なので、レイアウトは正方形に近い比率に寄せてある。
 */
export function CharacterCard({
  character,
  rank,
  points,
}: {
  character: Character;
  rank?: number;
  /** 大会の順位ポイント（ADR-012）。ランキングでだけ渡す。 */
  points?: number;
}) {
  const total = character.wins + character.losses + character.draws;
  const winRate = total > 0 ? character.wins / total : null;

  return (
    <article className={panel}>
      <header className="mb-3 flex items-baseline gap-2">
        {rank !== undefined && (
          <span className="tabular font-display text-sm text-text-muted">#{rank}</span>
        )}
        <h3 className="font-display flex-1 truncate" title={character.name}>
          {character.name}
        </h3>
        {/* ポイントは**順位表でだけ**意味を持つ（どの期間で数えたかで変わる。ADR-013）。
            赤は主要 CTA の専有なので、書体と大きさで立たせる（DESIGN.md §4）。 */}
        {points !== undefined && (
          <span className="tabular font-display shrink-0 text-sm text-text">
            {points.toLocaleString('ja-JP')}
            <span className="ml-0.5 text-xs text-text-muted">pt</span>
          </span>
        )}
      </header>

      <div className="mb-3 flex flex-wrap gap-1">
        {/* 系統は黄、特性は赤。design system が役割ごとに色を決めている（DESIGN.md §2）。 */}
        <span className={badgeYellow}>
          {ARCHETYPES[character.archetypeId]?.name ?? character.archetypeId}
        </span>
        {character.elementId && (
          <span className={badge}>
            {ELEMENTS[character.elementId]?.name ?? character.elementId}
          </span>
        )}
        {character.traitIds.map((id) => (
          <span key={id} className={badgeRed}>
            {TRAITS[id]?.name ?? id}
          </span>
        ))}
      </div>

      <div className="mb-3 text-xs text-text-muted">
        総合素質 <span className="tabular font-display">{character.potentialPoints}</span>
      </div>

      <dl className="mb-3 grid grid-cols-4 gap-1 text-center">
        {STAT_LABELS.map(([label, key]) => (
          <div key={key} className={`${sunken} py-1`}>
            <dt className="text-[10px] text-text-muted">{label}</dt>
            <dd className="tabular font-display text-sm">{character.stats[key]}</dd>
          </div>
        ))}
      </dl>

      <footer
        className={`${sunken} tabular flex flex-wrap items-baseline justify-between gap-x-3 px-2 py-1 text-xs text-text-muted`}
      >
        <span>
          {character.wins}勝 {character.losses}敗 {character.draws}分
        </span>
        {/* 0 戦のキャラクターに 0.0% と出すと「弱い」と誤読されるので出さない。 */}
        {/* 勝率は**色ではなく書体と大きさ**で立たせる。赤は主要 CTA の専有（DESIGN.md §4）。 */}
        {winRate !== null && (
          <span className="font-display text-sm text-text">勝率 {(winRate * 100).toFixed(1)}%</span>
        )}
      </footer>
    </article>
  );
}
